import { useMemo, useState, type ReactNode } from 'react';

import type { EvidenceState, PullRequestSummary } from '../api/types';
import { useFamilyScope } from '../components/family/FamilyScopeProvider';
import { PageHeader, queryFreshness, type FreshnessQuery } from '../components/pageHeader';
import { ScopedEmptyState } from '../components/family/ScopedEmptyState';
import { ErrorState, LoadingState } from '../components/state';
import { CONTROL_PLANE_MAX_LIMIT, useControlPlane } from '../hooks/useControlPlane';
import { useRepoChannels, EMPTY_CHANNELS } from '../hooks/useRepoChannels';
import { useRepoPullLists } from '../hooks/useRepoPullLists';
import { useAuth } from '../hooks/useAuth';
import { useFlow } from '../hooks/useFlow';
import { ExternalBranches } from './flow/ExternalBranches';
import { scopedExternalWork } from './flow/externalWorkModel';
import { useAttention } from '../hooks/usePipeline';
import { useForgeHost } from '../hooks/useForgeHost';
import { useRepositories } from '../hooks/useRepositories';
import { useShiftTodos } from '../hooks/useShift';
import { useRepoFamilies } from './needsYou/AttentionRow';
import { filterByFamily, urgentInArea } from './needsYou/needsYouModel';
import { useAttentionTodoIds } from './needsYou/useNeedsYou';
import { attentionByPull } from './pullAttentionModel';
import { PullRequestListView } from './PullRequestListView';
import { PullRequestTimeline } from './PullRequestTimeline';
import { awaitingReleaseCount } from './pullRepoGroupsModel';
import { pullGhostGroups, type GhostGroup } from './pullGhostsModel';
import { releaseLadder, UNKNOWN_LADDER } from './releaseChannelsModel';
import {
  ACTIVE_STATE_FILTER,
  DEFAULT_PULL_ROOM_FILTERS,
  PULL_ROOM_FILTER_PARAMS,
  filterPullRequests,
  filterPullSummaries,
  fromControlPullRequest,
  groupPullRequests,
  isBoardView,
  pullListState,
  repoOptions,
  scopedCounts,
  summaryCounts,
  timelineRepos,
  scopeToFamily,
  type PullRoomFilters,
} from './pullRoomModel';
import { pullCountsSentence } from './pullTimelineModel';
import { usePageTitle } from '../hooks/usePageTitle';
import { useViewState } from '../hooks/useViewState';

import './page.css';
import './PullRoomPage.css';

/** Checks, reviews and merges move without this tab doing anything: poll. */
const REFRESH_MS = 30_000;

const EVIDENCE_STATES: EvidenceState[] = [
  'fresh',
  'missing',
  'queued',
  'failed',
  'unknown',
];

/**
 * The page's header, the same while it loads, when the read fails and when the
 * rows are there, so "In flight" and the freshness stamp never move.
 */
function PullRoomHeader({
  snapshot,
  sentence,
  views
}: {
  snapshot: FreshnessQuery;
  sentence?: string;
  views?: ReactNode;
}): JSX.Element {
  return (
    <PageHeader
      title="In flight"
      className="pull-room__header"
      testId="pull-room-header"
      freshness={queryFreshness(snapshot)}
      actions={views}
    >
      {sentence ? (
        <p className="pull-room__sentence" data-testid="pull-room-sentence">
          {sentence}
        </p>
      ) : null}
    </PageHeader>
  );
}

export function PullRoomPage(): JSX.Element {
  usePageTitle('In flight');
  // The open work is what this page is for, so it asks for the largest page
  // the snapshot hands out rather than the default 100 rows per collection.
  const snapshot = useControlPlane({
    refetchInterval: REFRESH_MS,
    limit: CONTROL_PLANE_MAX_LIMIT,
  });
  // Every filter is in the URL, so the list a person is looking at is a link
  // and a shared one re-opens the same view.
  const view = useViewState();
  const filters = useMemo<PullRoomFilters>(
    () => ({
      repo: view.read(PULL_ROOM_FILTER_PARAMS.repo, DEFAULT_PULL_ROOM_FILTERS.repo),
      state: view.read(PULL_ROOM_FILTER_PARAMS.state, DEFAULT_PULL_ROOM_FILTERS.state),
      evidence: view.read(
        PULL_ROOM_FILTER_PARAMS.evidence,
        DEFAULT_PULL_ROOM_FILTERS.evidence
      ),
      checkPosture: view.read(
        PULL_ROOM_FILTER_PARAMS.checkPosture,
        DEFAULT_PULL_ROOM_FILTERS.checkPosture
      ),
      search: view.read(PULL_ROOM_FILTER_PARAMS.search, DEFAULT_PULL_ROOM_FILTERS.search),
    }),
    [view]
  );
  const repo = filters.repo;
  // Typing and the filter selects narrow one list: they replace, so Back
  // leaves the page rather than retracing a search box letter by letter.
  const setFilters = (patch: Partial<PullRoomFilters>): void => {
    const written: Record<string, string | null> = {};
    for (const key of Object.keys(patch) as (keyof PullRoomFilters)[]) {
      const value = patch[key] ?? DEFAULT_PULL_ROOM_FILTERS[key];
      written[PULL_ROOM_FILTER_PARAMS[key]] =
        value === DEFAULT_PULL_ROOM_FILTERS[key] ? null : value;
    }
    view.write(written, 'replace');
  };
  const scope = useFamilyScope();
  // Five controls for a handful of pull requests is more to read than the list
  // itself: they fold away unless one of them is doing something.
  const filtersActive =
    filters.repo !== DEFAULT_PULL_ROOM_FILTERS.repo ||
    filters.state !== DEFAULT_PULL_ROOM_FILTERS.state ||
    filters.evidence !== DEFAULT_PULL_ROOM_FILTERS.evidence ||
    filters.checkPosture !== DEFAULT_PULL_ROOM_FILTERS.checkPosture ||
    filters.search !== DEFAULT_PULL_ROOM_FILTERS.search;
  const [filtersOpen, setFiltersOpen] = useState(filtersActive);
  // The shell's family scope scopes everything on the page to that family's
  // repos, and is stated here as `?family=`. The repository list (already
  // cached for /repos) says which family a repo is in.
  const family = scope.family;
  const repositories = useRepositories({});
  const forgeHost = useForgeHost();
  const members = useMemo(
    () => repositories.data?.repositories ?? [],
    [repositories.data]
  );
  const families = useMemo(
    () =>
      new Map(
        members.map((member) => [
          `${member.id.owner}/${member.id.name}`,
          member.family ?? null,
        ])
      ),
    [members]
  );
  // The family is the header's scope. The board toggle is a view switch and
  // pushes, so Back returns to the view before it.
  const setRepo = (value: string): void => setFilters({ repo: value });
  // The timeline is the page; the lane board stays one click away.
  const board = isBoardView(view.read('view'));
  const setBoard = (next: boolean): void =>
    view.write({ view: next ? 'board' : null }, 'push');

  // The board shows work in flight. The timeline is a history — its bands run
  // from work that has not merged yet down to what production runs — so its
  // default asks for merged and closed pull requests too.
  const viewFilters = useMemo(
    () =>
      board || filters.state !== ACTIVE_STATE_FILTER ? filters : { ...filters, state: 'all' },
    [board, filters]
  );

  const everything = useMemo(
    () =>
      snapshot.data?.pullRequests.map((pr) => fromControlPullRequest(pr, forgeHost(pr.repo))) ?? [],
    [snapshot.data, forgeHost]
  );
  const items = useMemo(
    () => scopeToFamily(everything, family, families),
    [everything, family, families]
  );
  const filtered = useMemo(
    () => filterPullRequests(items, filters),
    [filters, items]
  );
  const lanes = useMemo(() => groupPullRequests(filtered), [filtered]);
  const repos = useMemo(() => repoOptions(items), [items]);
  // The snapshot says which repositories hold a matching pull request; only
  // those are asked for their lists, which carry review and merge state.
  // With a history filter every repository in scope is asked, as its own
  // Pull requests page would be; the snapshot alone only knows open ones.
  const wanted = useMemo(
    () =>
      timelineRepos(
        filterPullRequests(items, viewFilters),
        Array.from(families.keys()),
        {
          repo: filters.repo,
          family,
          history: pullListState(viewFilters.state) === undefined,
        },
        families
      ),
    [items, viewFilters, families, filters.repo, family]
  );
  const lists = useRepoPullLists(
    board ? [] : wanted.repos,
    pullListState(viewFilters.state),
    REFRESH_MS
  );
  const rows = useMemo(
    () => filterPullSummaries(lists.pulls, viewFilters),
    [viewFilters, lists.pulls]
  );

  // Where every merged change has got to: dev, canary, stable, production.
  // The tags of the repositories released only by tag are one batched read;
  // a repository the forge did not answer for reads "release unknown".
  const channels = useRepoChannels(board ? [] : wanted.repos, 'main');
  const ladderFor = (pr: PullRequestSummary) => {
    const key = `${pr.repo.owner}/${pr.repo.name}`;
    if (channels.deferred.has(key)) return UNKNOWN_LADDER;
    const entry = channels.byRepo.get(key) ?? EMPTY_CHANNELS;
    return releaseLadder(pr, entry.baselines, entry.compares);
  };

  /**
   * A todo names its repos bare (`jeryu-web`); a section is keyed `owner/name`.
   * Two repositories can share a name under different owners, so one that
   * holds a pull request wins over one that merely exists. The repositories
   * asked for their lists cannot break the tie any more: with a history filter
   * that is every repository in scope, which would make the winner whichever
   * sorted last.
   */
  const repoKeyFor = useMemo(() => {
    const byName = new Map<string, string>();
    for (const full of repoOptions(items)) {
      const name = full.split('/')[1];
      if (name && !byName.has(name)) byName.set(name, full);
    }
    for (const member of repositories.data?.repositories ?? []) {
      const full = `${member.id.owner}/${member.id.name}`;
      if (!byName.has(member.id.name)) byName.set(member.id.name, full);
    }
    return (repo: string): string | null =>
      repo.includes('/') ? repo : byName.get(repo) ?? null;
  }, [repositories.data, items]);

  // Shift work that has not opened a pull request yet, filed under its repo.
  const todos = useShiftTodos(undefined);
  // A ghost row is red when the Needs you list names its todo, so the row, the
  // strip above and the nav badge are one answer.
  const attentionTodoIds = useAttentionTodoIds();
  const ghosts: GhostGroup[] = useMemo(
    () =>
      board
        ? []
        : pullGhostGroups(todos.data?.todos ?? [], {
            now: new Date(),
            repos: repo === 'all' ? null : new Set([repo]),
            family,
            attentionTodoIds,
          }),
    [board, family, repo, todos.data, attentionTodoIds]
  );
  // What waits on a person here is marked on its own row; the rest is on
  // Needs you, which the nav badge counts.
  const { user } = useAuth();
  // Estimates on the rows; refused for a non-admin, which shows none.
  const flow = useFlow(family ? { family } : {});
  const attention = useAttention(user?.role === 'admin');
  const repoFamilies = useRepoFamilies();
  const waiting = useMemo(
    () => filterByFamily(urgentInArea(attention.data, 'pulls'), family, repoFamilies),
    [attention.data, family, repoFamilies]
  );
  const waitingByPull = useMemo(() => attentionByPull(waiting), [waiting]);

  if (snapshot.isLoading) {
    return (
      <div className="page pull-room" data-testid="pull-room-page">
        <PullRoomHeader snapshot={snapshot} />
        <LoadingState variant="message" title="Loading pull requests." />
      </div>
    );
  }

  if (snapshot.isError || !snapshot.data) {
    return (
      <div className="page pull-room" data-testid="pull-room-page">
        <PullRoomHeader snapshot={snapshot} />
        <ErrorState
          title="Could not load pull requests."
          error={snapshot.error}
          description={
            snapshot.error ? undefined : 'Pull requests are unavailable right now.'
          }
          onRetry={() => void snapshot.refetch()}
          testId="pull-room-error"
        />
      </div>
    );
  }

  // Unscoped, the counts are the server's: it counted every pull request,
  // while this page holds one page of the snapshot and 24 repositories of
  // lists. Scoped to a family or a repo, the loaded rows are the better
  // answer, and `wanted.skipped` says when even they are cut.
  const scoped = family !== '' || filters.repo !== 'all';
  const headerCounts = scoped
    ? scopedCounts(items)
    : summaryCounts(snapshot.data.summary);

  return (
    <div className="page page--full pull-room" data-testid="pull-room-page">
      <PullRoomHeader
        snapshot={snapshot}
        sentence={pullCountsSentence(
          headerCounts,
          board ? undefined : awaitingReleaseCount(rows, ladderFor)
        )}
        views={
          <div className="pull-room__views" role="group" aria-label="View">
            <button type="button" aria-pressed={!board} onClick={() => setBoard(false)}>
              Timeline
            </button>
            <button type="button" aria-pressed={board} onClick={() => setBoard(true)}>
              Board
            </button>
          </div>
        }
      />

      {scopedExternalWork(flow.data?.external_work?.snapshots ?? [], family, filters.repo).map((external) => (
        <ExternalBranches key={external.source_repo} snapshot={external} />
      ))}
      {(flow.data?.external_work?.problems ?? []).map((problem) => <p role="status" key={problem}>{problem}</p>)}

      <details
        className="pull-room__filter-fold"
        open={filtersOpen}
        onToggle={(event) => setFiltersOpen(event.currentTarget.open)}
      >
        <summary>{filtersActive ? 'Filters (on)' : 'Filters'}</summary>
      <section className="pull-room__filters" aria-label="Pull request filters">
        <label>
          Repo
          <select
            value={filters.repo}
            onChange={(event) => setRepo(event.target.value)}
          >
            <option value="all">All repos</option>
            {repos.map((repo) => (
              <option key={repo} value={repo}>
                {repo}
              </option>
            ))}
          </select>
        </label>
        <label>
          State
          <select
            value={filters.state}
            onChange={(event) =>
              setFilters({ state: event.target.value })
            }
          >
            <option value="active">Default (timeline adds release history)</option>
            <option value="all">All states</option>
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="mergeable">Mergeable</option>
            <option value="merged">Merged</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label>
          Evidence
          <select
            value={filters.evidence}
            onChange={(event) =>
              setFilters({ evidence: event.target.value })
            }
          >
            <option value="all">All evidence</option>
            {EVIDENCE_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </label>
        <label>
          Checks
          <select
            value={filters.checkPosture}
            onChange={(event) =>
              setFilters({ checkPosture: event.target.value })
            }
          >
            <option value="all">All checks</option>
            <option value="missing">Missing</option>
            <option value="failing">Failing</option>
            <option value="queued">Queued</option>
            <option value="running">Running</option>
            <option value="passing">Passing</option>
          </select>
        </label>
        <label className="pull-room__search">
          Search
          <input
            value={filters.search}
            onChange={(event) =>
              setFilters({ search: event.target.value })
            }
            type="search"
            aria-label="Search pull requests"
          />
        </label>
      </section>
      </details>

      <div className="pull-room__content">
        {board ? (
          <PullRequestListView
            lanes={lanes}
            emptyMessage="No pull requests match the current filters."
          />
        ) : (
          <>
            {lists.failed.map((failure) => (
              <p key={failure.repo} className="pull-room__aside" role="status">
                {failure.repo} did not answer: {failure.message}
              </p>
            ))}
            {wanted.skipped > 0 ? (
              <p className="pull-room__aside">
                Showing the first {wanted.repos.length} repositories; {wanted.skipped} more match.
                Narrow by family or repo to see them.
              </p>
            ) : null}
            {rows.length === 0 && ghosts.length === 0 && lists.loading.length > 0 ? (
              <LoadingState variant="message" title="Loading pull requests." />
            ) : rows.length === 0 && ghosts.length === 0 ? (
              <div className="pull-list__empty" data-testid="pull-room-empty">
                <ScopedEmptyState
                  title={
                    filtersActive
                      ? 'No pull requests match the current filters.'
                      : 'No open pull requests.'
                  }
                />
              </div>
            ) : (
              <PullRequestTimeline
                pulls={rows}
                emptyMessage="No open pull requests."
                showRepo
                ladderFor={ladderFor}
                ghosts={ghosts}
                repoKeyFor={repoKeyFor}
                attention={waitingByPull}
                flow={flow.data}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
